<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import WahaCallPanel from './WahaCallPanel.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ConversationLabelsAction from '../../buttons/ConversationLabelsAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { useWahaCall } from 'dashboard/composables/useWahaCall';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const { isActive: isCallActive, initiateCall } = useWahaCall();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const currentInbox = computed(() => {
  if (!currentChat.value?.inbox_id) return null;
  return store.getters['inboxes/getInbox'](currentChat.value.inbox_id);
});

const isWaCallsInbox = computed(
  () => currentInbox.value?.additional_attributes?.is_wacalls === true
);

const waCallsSessionId = computed(
  () =>
    currentInbox.value?.additional_attributes?.wacalls_session_id ||
    currentInbox.value?.channel_id
);

function openWaCallsPanel() {
  if (isCallActive.value) return;
  const chat = currentChat.value;
  const phone =
    chat.meta?.sender?.phone_number || chat.contact_inbox?.source_id || '';
  const name = chat.meta?.sender?.name || phone;
  const sid = waCallsSessionId.value || 'default';
  initiateCall(sid, phone.replace(/[^0-9]/g, ''), name);
}

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ButtonV4
      v-if="isWaCallsInbox"
      v-tooltip="$t('WACALLS.CALL_BUTTON_TOOLTIP')"
      size="sm"
      variant="ghost"
      color="slate"
      icon="i-lucide-phone-call"
      class="rounded-md hover:bg-n-alpha-2"
      :disabled="isCallActive"
      @click="openWaCallsPanel"
    />
    <WahaCallPanel v-if="isCallActive" />
    <ButtonV4
      v-tooltip="$t('CONVERSATION.SELECTION_MODE.SELECT')"
      size="sm"
      variant="ghost"
      color="slate"
      icon="i-lucide-check-square"
      class="rounded-md hover:bg-n-alpha-2"
      @click="emitter.emit('TOGGLE_MESSAGE_SELECTION_MODE')"
    />
    <ConversationLabelsAction />
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
